import React, { useState, useEffect, useMemo } from 'react';
import { useListasConfig } from './useListasConfig';
import { db } from '../firebase';
import {
  collection, addDoc, updateDoc, doc,
  serverTimestamp, getDoc
} from 'firebase/firestore';
import { useColeccion, BarraActualizacion, useEstadoPersistente, useAlCambiar, usePosicionScroll } from './DatosContext';
import DialogoConfirmar from './Dialogo';
import useTecladoLista, { ESTILO_ACTIVO } from './useTecladoLista';
import FichaOportunidad, { OPCIONES_ESTADO_MANUAL, estadoEditableOp } from './FichaOportunidad';

// ── CONSTANTES ───────────────────────────────────────────────────────────────

const ESTADOS_OPORTUNIDAD = [
  { valor: 'pendiente',    etiqueta: 'Pendiente',    color: '#534AB7' },
  { valor: 'presupuestado', etiqueta: 'Presupuestado', color: '#BA7517' },
  { valor: 'aceptado',    etiqueta: 'Aceptado',    color: '#1D9E75' },
  { valor: 'rechazado',   etiqueta: 'Rechazado',   color: '#D85A30' },
  { valor: 'descartado',  etiqueta: 'Descartado',  color: '#6b7280' },
];

function estadoOportunidad(valor) {
  return ESTADOS_OPORTUNIDAD.find(e => e.valor === valor) || ESTADOS_OPORTUNIDAD[0];
}

function EstadoBadge({ estado }) {
  const found = estadoOportunidad(estado);
  return (
    <span style={{
      background: found.color, color: '#fff',
      fontSize: '0.72rem', fontWeight: '500',
      padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap',
    }}>
      {found.etiqueta}
    </span>
  );
}

const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function formatearFechaCorta(fecha) {
  if (!fecha) return '—';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function formatearEuros(valor) {
  if (!valor) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(valor);
}
const fmtNum = (n) => n ? new Intl.NumberFormat('es-ES').format(n) : '—';

function KpiConversion({ label, num, den, descripcion }) {
  const pct = den > 0 ? Math.round((num / den) * 1000) / 10 : 0;
  return (
    <div style={styles.kpiCard}>
      <p style={styles.kpiLabel}>{label}</p>
      <p style={{ ...styles.kpiValor, fontSize: '1.4rem' }}>
        {pct.toFixed(1).replace('.', ',')}%
        <span style={{ fontSize: '0.8rem', color: '#AFA9EC', marginLeft: '6px' }}>
          ({num}/{den})
        </span>
      </p>
      {descripcion && <p style={{ color: '#AFA9EC', fontSize: '0.72rem', marginTop: '4px' }}>{descripcion}</p>}
    </div>
  );
}

function ModalConfirmar({ titulo, mensaje, onConfirmar, onCancelar, textoCancelar, textoConfirmar }) {
  // Sesión 34: todos los avisos usan el diálogo común (la opción segura es la destacada; Enter/Esc la eligen)
  const esBorrado = /eliminar|eliminando/i.test(textoConfirmar || '');
  return (
    <DialogoConfirmar
      titulo={titulo || 'Cambios sin guardar'}
      mensaje={mensaje}
      textoSeguro={textoCancelar && textoCancelar !== 'Cancelar' ? textoCancelar : (esBorrado ? 'No eliminar' : 'Seguir editando')}
      textoPeligroso={textoConfirmar || 'Salir sin guardar'}
      onSeguro={onCancelar}
      onPeligroso={onConfirmar}
      ocupado={/^eliminando/i.test(textoConfirmar || '')}
    />
  );
}

const estiloInput = () => ({
  background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)',
  borderRadius: '8px', padding: '10px 14px', color: '#fff',
  fontSize: '0.88rem', outline: 'none', width: '100%',
  fontFamily: 'DM Sans, sans-serif', boxSizing: 'border-box',
});

function Campo({ label, children, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={{ color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
      {children}
    </div>
  );
}

// ── FORMULARIO NUEVA OPORTUNIDAD ─────────────────────────────────────────────

function FormularioNuevaOportunidad({ leads, onGuardar, onCancelar }) {
  const { sectores } = useListasConfig(['sectores']);
  const [leadSeleccionado, setLeadSeleccionado] = useState('');
  const [busquedaLead, setBusquedaLead] = useState('');
  const [form, setForm] = useState({
    fecha_entrada: new Date().toISOString().split('T')[0],
    estado: 'pendiente', sector: '',
    direccion: '', cp: '', ciudad: '', provincia: '', pais: 'España',
    superficie: '', presupuesto_estimado: '', observaciones: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const leadsFiltrados = leads.filter(l => {
    const t = norm(busquedaLead);
    return !t || norm(`${l.nombre || ''} ${l.apellidos || ''}`).includes(t) || norm(l.empresa).includes(t);
  }).slice(0, 8);
  const elegirLead = (l) => { setLeadSeleccionado(l.id); setBusquedaLead(''); };
  const teclaLead = useTecladoLista(busquedaLead ? leadsFiltrados : [], elegirLead, () => setBusquedaLead(''));

  const leadElegido = leads.find(l => l.id === leadSeleccionado);

  const guardar = async () => {
    if (!leadSeleccionado) return;
    setGuardando(true);
    try {
      const docRef = await addDoc(collection(db, 'leads', leadSeleccionado, 'posibles_proyectos'), {
        ...form,
        fecha_entrada: new Date(form.fecha_entrada),
        superficie: form.superficie ? parseFloat(form.superficie) : null,
        presupuesto_estimado: form.presupuesto_estimado ? parseFloat(form.presupuesto_estimado) : null,
        creado_en: serverTimestamp(),
      });
      const snap = await getDoc(docRef);
      onGuardar({
        id: snap.id,
        leadId: leadSeleccionado,
        ...snap.data(),
      });
    } catch (e) { console.error(e); setGuardando(false); }
  };

  return (
    <div style={{ ...styles.container, paddingBottom: '100px' }}>
      {modal && <ModalConfirmar mensaje="¿Quieres salir sin guardar esta oportunidad?" onConfirmar={onCancelar} onCancelar={() => setModal(false)} />}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={styles.botonVolver} onClick={() => setModal(true)}>← Oportunidades</button>
          <h1 style={styles.titulo}>Nueva oportunidad</h1>
        </div>
      </div>

      <div style={{ padding: '24px 32px 32px' }}>
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Lead asociado *</h2>
          {leadElegido ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid #1D9E75' }}>
              <div>
                <span style={{ color: '#ffffff', fontWeight: '500' }}>{leadElegido.nombre} {leadElegido.apellidos || ''}</span>
                {leadElegido.empresa && <span style={{ color: '#AFA9EC', fontSize: '0.82rem', marginLeft: '8px' }}>{leadElegido.empresa}</span>}
              </div>
              <button style={styles.botonEditar} onClick={() => { setLeadSeleccionado(''); setBusquedaLead(''); }}>Cambiar</button>
            </div>
          ) : (
            <div>
              <input style={styles.buscador} placeholder="Buscar lead por nombre o empresa..." value={busquedaLead} onChange={e => setBusquedaLead(e.target.value)} onKeyDown={teclaLead.onKeyDown} />
              {busquedaLead && leadsFiltrados.length > 0 && (
                <div style={{ background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                  {leadsFiltrados.map((l, i) => (
                    <button key={l.id} onMouseEnter={() => teclaLead.setActivo(i)} style={{ display: 'block', width: '100%', background: i === teclaLead.activo ? ESTILO_ACTIVO.background : 'transparent', border: 'none', borderBottom: '1px solid rgba(83,74,183,0.1)', padding: '10px 14px', color: '#fff', fontSize: '0.88rem', textAlign: 'left', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                      onClick={() => elegirLead(l)}>
                      {l.nombre} {l.apellidos || ''} {l.empresa ? `· ${l.empresa}` : ''}
                    </button>
                  ))}
                </div>
              )}
              {busquedaLead && leadsFiltrados.length === 0 && (
                <p style={{ color: '#AFA9EC', fontSize: '0.82rem', marginTop: '8px' }}>No se encontraron leads</p>
              )}
            </div>
          )}
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Datos de la oportunidad</h2>
          <div style={styles.camposGrid}>
            <Campo label="Fecha de contacto">
              <input type="date" className="metron-date" style={{ ...estiloInput(), colorScheme: 'dark' }} value={form.fecha_entrada} onChange={e => set('fecha_entrada', e.target.value)} />
            </Campo>
            <Campo label="Estado">
              <select style={{ ...estiloInput(), background: '#1a1440' }} value={form.estado} onChange={e => set('estado', e.target.value)}>
                {OPCIONES_ESTADO_MANUAL.map(e => <option key={e.valor} value={e.valor}>{e.etiqueta}</option>)}
              </select>
            </Campo>
            <Campo label="Sector">
              <select style={{ ...estiloInput(), background: '#1a1440' }} value={form.sector || ''} onChange={e => set('sector', e.target.value)}>
                <option value="">Sin sector</option>
                {sectores.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Campo>
            <Campo label="Superficie estimada (m²)">
              <input type="number" style={{ ...estiloInput(), colorScheme: 'dark' }} value={form.superficie} onChange={e => set('superficie', e.target.value)} placeholder="0" />
            </Campo>
            <Campo label="Presupuesto estimado (€)">
              <input type="number" style={{ ...estiloInput(), colorScheme: 'dark' }} value={form.presupuesto_estimado} onChange={e => set('presupuesto_estimado', e.target.value)} placeholder="0" />
            </Campo>
            <Campo label="Ciudad">
              <input style={estiloInput()} value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Ciudad" />
            </Campo>
            <Campo label="Provincia">
              <input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" />
            </Campo>
            <Campo label="Observaciones" fullWidth>
              <textarea style={{ ...estiloInput(), resize: 'vertical', minHeight: '100px' }} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Detalles del posible proyecto..." />
            </Campo>
          </div>
        </div>
      </div>

      <div style={styles.barraGuardar}>
        <button style={styles.botonSecundario} onClick={() => setModal(true)}>Cancelar</button>
        <button style={styles.botonPrimario} onClick={guardar} disabled={!leadSeleccionado || guardando}>
          {guardando ? 'Guardando...' : 'Crear oportunidad'}
        </button>
      </div>
    </div>
  );
}

// ── LISTADO DE OPORTUNIDADES ─────────────────────────────────────────────────

const OP_POR_PAGINA = 50;

function ListadoOportunidades({ oportunidades, leads, onEditarOportunidad, onNavegarLead, onCambiarEstado }) {
  const { sectores } = useListasConfig(['sectores']);
  // Sesión 32 · Entrega B: filtros, filas visibles y scroll se recuerdan durante la sesión
  const [filtroEstado, setFiltroEstado] = useEstadoPersistente('oportunidades.filtroEstado', '');
  const [filtroSector, setFiltroSector] = useEstadoPersistente('oportunidades.filtroSector', '');
  const [filtroAnio, setFiltroAnio] = useEstadoPersistente('oportunidades.filtroAnio', '');
  const [busqueda, setBusqueda] = useEstadoPersistente('oportunidades.busqueda', '');
  const [visibles, setVisibles] = useEstadoPersistente('oportunidades.visibles', OP_POR_PAGINA);
  usePosicionScroll('oportunidades');

  // Años disponibles
  const aniosDisponibles = [...new Set(oportunidades.map(o => {
    const f = o.fecha_entrada?.toDate ? o.fecha_entrada.toDate() : (o.fecha_entrada ? new Date(o.fecha_entrada) : null);
    return f ? f.getFullYear() : null;
  }).filter(Boolean))].sort((a, b) => b - a);

  // Reset paginación al cambiar filtros
  useAlCambiar(() => setVisibles(OP_POR_PAGINA), [filtroEstado, filtroSector, filtroAnio, busqueda]);

  const filtradas = oportunidades.filter(o => {
    const coincideEstado = !filtroEstado || o.estado === filtroEstado;
    const coincideSector = !filtroSector || o.sector === filtroSector;
    const coincideTexto = !busqueda ||
      norm(o.leadNombre).includes(norm(busqueda)) ||
      norm(o.leadEmpresa).includes(norm(busqueda)) ||
      norm(o.ciudad).includes(norm(busqueda)) ||
      norm(o.sector).includes(norm(busqueda));
    const coincideAnio = !filtroAnio || (() => {
      const f = o.fecha_entrada?.toDate ? o.fecha_entrada.toDate() : (o.fecha_entrada ? new Date(o.fecha_entrada) : null);
      return f ? f.getFullYear() === parseInt(filtroAnio) : false;
    })();
    return coincideEstado && coincideSector && coincideTexto && coincideAnio;
  }).sort((a, b) => (b.fecha_entrada?.seconds || 0) - (a.fecha_entrada?.seconds || 0));

  const hayFiltros = filtroEstado || filtroSector || filtroAnio || busqueda;

  // ── KPIs de conversión — solo año y sector, nunca estado ni búsqueda ──
  // La tasa de presupuestación y conversión miden el pipeline completo del periodo/sector
  // No tienen sentido filtradas por estado (si filtras Aceptado siempre sería 100%)
  const baseKpi = oportunidades.filter(o => {
    const coincideSector = !filtroSector || o.sector === filtroSector;
    const coincideAnio = !filtroAnio || (() => {
      const f = o.fecha_entrada?.toDate ? o.fecha_entrada.toDate() : (o.fecha_entrada ? new Date(o.fecha_entrada) : null);
      return f ? f.getFullYear() === parseInt(filtroAnio) : false;
    })();
    return coincideSector && coincideAnio;
  });
  const totalBase = baseKpi.length;

  const nPendiente     = baseKpi.filter(o => o.estado === 'pendiente').length;
  const nPresupuestado = baseKpi.filter(o => o.estado === 'presupuestado').length;
  const nAceptado      = baseKpi.filter(o => o.estado === 'aceptado').length;
  const nRechazado     = baseKpi.filter(o => o.estado === 'rechazado').length;
  const nDescartado    = baseKpi.filter(o => o.estado === 'descartado').length;

  // Presupuestadas = todas las que alguna vez llegaron a presupuesto
  // (presupuestado actualmente + aceptadas + rechazadas — pendiente excluida porque aún no hay presupuesto emitido)
  const nPresupuestadas = nPresupuestado + nAceptado + nRechazado;



  return (
    <>
      {/* KPIs de conteo */}
      <div style={styles.gridKpis}>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Total{hayFiltros ? ' (filtrado)' : ''}</p>
          <p style={styles.kpiValor}>{totalBase}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Pendientes</p>
          <p style={{ ...styles.kpiValor, color: '#534AB7' }}>{nPendiente}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Presupuestadas</p>
          <p style={{ ...styles.kpiValor, color: '#BA7517' }}>{nPresupuestado}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Aceptadas</p>
          <p style={{ ...styles.kpiValor, color: '#1D9E75' }}>{nAceptado}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Rechazadas</p>
          <p style={{ ...styles.kpiValor, color: '#D85A30' }}>{nRechazado}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Descartadas</p>
          <p style={{ ...styles.kpiValor, color: '#6b7280' }}>{nDescartado}</p>
        </div>
      </div>

      {/* KPIs de conversión */}
      <div style={{ ...styles.gridKpis, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginBottom: '24px' }}>
        <KpiConversion
          label="Tasa de presupuestación"
          num={nPresupuestadas}
          den={totalBase}
          descripcion="Oportunidades que llegaron a generar un presupuesto"
        />
        <KpiConversion
          label="Conversión sobre presupuestadas"
          num={nAceptado}
          den={nPresupuestadas}
          descripcion="Proyectos aceptados sobre oportunidades presupuestadas"
        />
        <KpiConversion
          label="Conversión global"
          num={nAceptado}
          den={totalBase}
          descripcion="Proyectos aceptados sobre total de oportunidades"
        />
      </div>

      {/* Filtros */}
      <div style={styles.filtrosBar}>
        <input
          style={styles.buscador}
          placeholder="Buscar por lead, empresa, ciudad, sector..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
        <div style={styles.pills}>
          {ESTADOS_OPORTUNIDAD.map(e => (
            <button key={e.valor} style={{
              ...styles.pill,
              background: filtroEstado === e.valor ? e.color : 'transparent',
              color: filtroEstado === e.valor ? '#fff' : '#AFA9EC',
              borderColor: filtroEstado === e.valor ? e.color : 'rgba(83,74,183,0.3)',
            }} onClick={() => setFiltroEstado(filtroEstado === e.valor ? '' : e.valor)}>
              {e.etiqueta}
            </button>
          ))}
        </div>
        <select style={styles.select} value={filtroSector} onChange={e => setFiltroSector(e.target.value)}>
          <option value="">Todos los sectores</option>
          {sectores.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={styles.select} value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)}>
          <option value="">Todos los años</option>
          {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        {hayFiltros && (
          <button style={styles.botonLimpiar} onClick={() => { setFiltroEstado(''); setFiltroSector(''); setFiltroAnio(''); setBusqueda(''); }}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Tabla */}
      {filtradas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#AFA9EC' }}>
          {hayFiltros ? 'No hay oportunidades que coincidan con los filtros.' : 'Aún no hay oportunidades registradas.'}
        </div>
      ) : (
        <>
          <div style={styles.tablaWrapper}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  {['Lead', 'Sector', 'Ubicación', 'Superficie', 'Presup. est.', 'Estado', 'Fecha', ''].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtradas.slice(0, visibles).map(op => (
                  <tr key={op.id} style={{ ...styles.tr, cursor: 'pointer' }} onClick={() => onEditarOportunidad(op)}>
                    <td style={styles.td}>
                      <span style={styles.nombreLead}>{op.leadNombre}</span>
                      {op.leadEmpresa && <span style={styles.subLead}>{op.leadEmpresa}</span>}
                    </td>
                    <td style={styles.td}>{op.sector || '—'}</td>
                    <td style={styles.td}>{[op.ciudad, op.provincia].filter(Boolean).join(', ') || '—'}</td>
                    <td style={styles.td}>{op.superficie ? `${fmtNum(op.superficie)} m²` : '—'}</td>
                    <td style={styles.td}>{formatearEuros(op.presupuesto_estimado)}</td>
                    <td style={styles.td} onClick={e => { if (estadoEditableOp(op.estado)) e.stopPropagation(); }}>
                      {/* Sesión 34: solo se cambia aquí entre Pendiente y Descartada (sin presupuestos). Con presupuestos,
                          el estado sale de ellos y se muestra sin selector. */}
                      {estadoEditableOp(op.estado) ? (
                        <select
                          style={{
                            background: estadoOportunidad(op.estado).color,
                            color: '#fff', border: 'none', borderRadius: '20px',
                            padding: '4px 10px', fontSize: '0.75rem', fontWeight: '500',
                            cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', outline: 'none',
                          }}
                          value={op.estado || 'pendiente'}
                          onChange={e => onCambiarEstado(op, e.target.value)}
                        >
                          {OPCIONES_ESTADO_MANUAL.map(e => (
                            <option key={e.valor} value={e.valor} style={{ background: '#1a1440', color: '#ffffff' }}>
                              {e.etiqueta}
                            </option>
                          ))}
                        </select>
                      ) : <EstadoBadge estado={op.estado} />}
                    </td>
                    <td style={styles.td}>{formatearFechaCorta(op.fecha_entrada)}</td>
                    <td style={styles.td} onClick={e => e.stopPropagation()}>
                      <button style={styles.botonVer} onClick={() => {
                        const lead = leads.find(l => l.id === op.leadId);
                        if (lead && onNavegarLead) onNavegarLead(lead);
                      }}>
                        Ver lead →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {visibles < filtradas.length && (
            <div style={{ textAlign: 'center', padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <button style={styles.botonMostrarMas} onClick={() => setVisibles(v => v + OP_POR_PAGINA)}>
                Mostrar 50 más
              </button>
              <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>
                Mostrando {Math.min(visibles, filtradas.length)} de {filtradas.length} — usa el buscador para filtrar
              </span>
            </div>
          )}
          {visibles >= filtradas.length && filtradas.length > OP_POR_PAGINA && (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>
                Mostrando {filtradas.length} de {filtradas.length}
              </span>
            </div>
          )}
        </>
      )}
    </>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

function Oportunidades({ esAdmin, accionInicial, onAccionCompletada, onNavegarLead, onNavegar }) {
  const [vista, setVista] = useState('listado');
  const [oportunidadSeleccionada, setOportunidadSeleccionada] = useState(null);
  // Sesión 32 · Entrega B: oportunidades y leads vienen del almacén compartido (una descarga por sesión).
  // Los documentos se guardan crudos; el nombre y la empresa del lead se derivan aquí.
  const { items: opsCrudas, setItems: setOportunidades, cargando: cargandoOps } = useColeccion('oportunidades');
  const { items: leads, cargando: cargandoLeads } = useColeccion('leads');
  const cargando = cargandoOps || cargandoLeads;
  const oportunidades = useMemo(() => {
    const leadMap = {};
    leads.forEach(l => { leadMap[l.id] = l; });
    return opsCrudas.map(o => {
      const lead = leadMap[o.leadId] || {};
      return { ...o, leadNombre: `${lead.nombre || ''} ${lead.apellidos || ''}`.trim(), leadEmpresa: lead.empresa };
    });
  }, [opsCrudas, leads]);

  // Sesión 34: abrir una oportunidad concreta desde otro módulo (Dashboard: pendientes y últimas oportunidades)
  const [abrirOp, setAbrirOp] = useState(null); // { id, leadId }
  useEffect(() => {
    if (!accionInicial) return;
    if (accionInicial.accion === 'nueva') {
      setVista('nueva');
      if (onAccionCompletada) onAccionCompletada();
    } else if (accionInicial.accion === 'ficha' && accionInicial.id) {
      setAbrirOp({ id: accionInicial.id, leadId: accionInicial.leadId || null });
      if (onAccionCompletada) onAccionCompletada();
    }
  }, [accionInicial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!abrirOp || cargando) return;
    const { id, leadId } = abrirOp;
    setAbrirOp(null);
    const enMemoria = oportunidades.find(o => o.id === id);
    if (enMemoria) { setOportunidadSeleccionada(enMemoria); setVista('ficha'); return; }
    // No está en la copia de esta sesión (creada por otra persona hace poco): se lee del servidor (1 lectura)
    if (!leadId) return;
    getDoc(doc(db, 'leads', leadId, 'posibles_proyectos', id)).then(snap => {
      if (!snap.exists()) return;
      const lead = leads.find(l => l.id === leadId) || {};
      setOportunidadSeleccionada({ id: snap.id, ...snap.data(), leadId, leadNombre: `${lead.nombre || ''} ${lead.apellidos || ''}`.trim(), leadEmpresa: lead.empresa });
      setVista('ficha');
    }).catch(e => console.error('No se pudo abrir la oportunidad', e));
  }, [abrirOp, cargando, oportunidades, leads]);

  const volver = () => { setVista('listado'); setOportunidadSeleccionada(null); };

  const onGuardarNueva = (nuevaOp) => {
    setOportunidades(prev => [nuevaOp, ...prev]);
    volver();
  };


  if (cargando) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#AFA9EC' }}>Cargando oportunidades...</div>;
  }

  if (vista === 'nueva') return (
    <FormularioNuevaOportunidad
      leads={leads}
      onGuardar={onGuardarNueva}
      onCancelar={volver}
    />
  );

  // Sesión 34: ficha común en modo lectura (Editar para cambiar), la misma que en la ficha del lead
  if (vista === 'ficha' && oportunidadSeleccionada) return (
    <FichaOportunidad
      key={oportunidadSeleccionada.id}
      oportunidad={oportunidadSeleccionada}
      esAdmin={esAdmin}
      textoVolver="Oportunidades"
      onVolver={volver}
      onActualizada={(op) => setOportunidadSeleccionada(op)}
      onEliminada={() => volver()}
      onNavegar={onNavegar}
    />
  );


  return (
    <div style={styles.container}>
      <div style={styles.cabecera}>
        <h1 style={styles.titulo}>Oportunidades</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <BarraActualizacion colecciones={['oportunidades', 'leads']} />
          <button style={styles.botonPrimario} onClick={() => setVista('nueva')}>+ Nueva oportunidad</button>
        </div>
      </div>

      <div style={{ padding: '24px 32px 32px' }}>
        <ListadoOportunidades
          oportunidades={oportunidades}
          leads={leads}
          onEditarOportunidad={(op) => { setOportunidadSeleccionada(op); setVista('ficha'); }}
          onNavegarLead={onNavegarLead}
          onCambiarEstado={async (op, nuevoEstado) => {
            try {
              await updateDoc(doc(db, 'leads', op.leadId, 'posibles_proyectos', op.id), { estado: nuevoEstado });
              setOportunidades(prev => prev.map(o => o.id === op.id ? { ...o, estado: nuevoEstado } : o));
            } catch (e) { console.error(e); }
          }}
        />
      </div>
    </div>
  );
}

// ── ESTILOS ──────────────────────────────────────────────────────────────────

const styles = {
  container:      { padding: '0', maxWidth: '100%' },
  cabecera:       { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: '1px solid rgba(83,74,183,0.2)', padding: '20px 32px', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
  titulo:         { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300', letterSpacing: '0.02em' },
  botonPrimario:  { background: '#534AB7', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSecundario:{ background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonVolver:    { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.88rem', cursor: 'pointer', padding: '0', fontFamily: 'DM Sans, sans-serif' },
  botonEditar:    { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  botonLimpiar:   { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminar:  { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonVer:       { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonMostrarMas:{ background: 'transparent', color: '#7B6FE8', border: '1px solid rgba(123,111,232,0.4)', borderRadius: '8px', padding: '10px 24px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  barraGuardar:   { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1440', borderTop: '1px solid rgba(83,74,183,0.3)', padding: '16px 32px', display: 'flex', justifyContent: 'flex-end', gap: '12px', zIndex: 100 },
  gridKpis:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' },
  kpiCard:        { background: '#1a1440', borderRadius: '10px', padding: '16px 20px', border: '1px solid rgba(83,74,183,0.2)' },
  kpiLabel:       { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' },
  kpiValor:       { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300' },
  filtrosBar:     { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', alignItems: 'center' },
  buscador:       { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '0.88rem', outline: 'none', minWidth: '240px', flex: '1', fontFamily: 'DM Sans, sans-serif' },
  pills:          { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pill:           { border: '1px solid', borderRadius: '20px', padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', background: 'transparent' },
  select:         { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#AFA9EC', fontSize: '0.88rem', outline: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  tablaWrapper:   { overflowX: 'auto' },
  tabla:          { width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' },
  th:             { color: '#AFA9EC', fontWeight: '500', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 14px', borderBottom: '1px solid rgba(83,74,183,0.2)', textAlign: 'left', whiteSpace: 'nowrap' },
  tr:             { borderBottom: '1px solid rgba(83,74,183,0.1)', transition: 'background 0.15s' },
  td:             { padding: '12px 14px', color: '#ffffff', verticalAlign: 'middle' },
  nombreLead:     { display: 'block', fontWeight: '500' },
  subLead:        { display: 'block', color: '#AFA9EC', fontSize: '0.78rem' },
  seccion:        { marginBottom: '28px' },
  seccionTitulo:  { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid rgba(83,74,183,0.15)' },
  camposGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' },
};

export default Oportunidades;
